/* SPDX-License-Identifier: AGPL-3.0-or-later */

import {Component} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import {TournamentDto, TournamentSignupTeamResponseDto, TournamentUpdateTeamDto} from 'openapi-generated';
import {catchError, finalize, of} from 'rxjs';
import {MatSnackBar} from '@angular/material/snack-bar';
import {TournamentEndpointService} from '@api';
import {HttpErrorResponse} from '@angular/common/http';
import LivesAtEnum = TournamentUpdateTeamDto.LivesAtEnum;
import {Globals} from "../../../global/globals";

@Component({
    selector: 'app-team-signup',
    templateUrl: './team-signup.component.html',
    styleUrl: './team-signup.component.scss',
})
export class TeamSignupComponent {
    tournament: TournamentDto | undefined;
    token: string | null | undefined;
    hideRequiredMarker= true;

    success = false;
    submitting = false;

    nameFormControl = new FormControl<string>('', [
        Validators.minLength(3),
        Validators.maxLength(25),
        Validators.required,
    ]);
    person1NameFormControl = new FormControl<string>('', [
        Validators.minLength(1),
        Validators.maxLength(100),
        Validators.required,
    ]);
    person2NameFormControl = new FormControl<string>('', [
        Validators.minLength(1),
        Validators.maxLength(100),
        Validators.required,
    ]);
    contactPhoneNumberFormControl = new FormControl<string>('', [
        Validators.minLength(1),
        Validators.maxLength(16),
        Validators.pattern(/(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/),
        Validators.required,
    ]);
    contactEmailFormControl = new FormControl<string>('', [
        Validators.required,
        Validators.email
    ]);
    livesAtFormControl = new FormControl<LivesAtEnum | null>(null, [Validators.required])

    newTeamNameForm = new FormGroup(
        {
            name: this.nameFormControl,
            person1Name: this.person1NameFormControl,
            person2Name: this.person2NameFormControl,
            contactPhoneNumber: this.contactPhoneNumberFormControl,
            contactEmail: this.contactEmailFormControl,
            livesAt: this.livesAtFormControl
        },
    );

    get isTournamentLoadedAndRegistrationOpen(): boolean {
        if (!this.tournament?.registrationEnd) {
            return false;
        }
        return new Date() < new Date(this.tournament.registrationEnd);
    }

    constructor(
        private tournamentService: TournamentEndpointService,
        private route: ActivatedRoute,
        private snackBar: MatSnackBar,
        private globals: Globals,
    ) {
        this.tournamentService.get(globals.mainTournamentId).subscribe(tournament => {
            this.tournament = tournament;
            this.token = globals.mainTournamentToken;
        });
    }

    async createTeam() {
        if (!this.newTeamNameForm.valid || this.submitting) {
            return;
        }
        this.submitting = true;

        const request = this.tournamentService.signupTeamForTournament(
            this.tournament!.id!,
            {
                name: this.nameFormControl.value!,
                person1Name: this.person1NameFormControl.value!,
                person2Name: this.person2NameFormControl.value!,
                contactPhoneNumber: this.contactPhoneNumberFormControl.value!,
                contactEmail: this.contactEmailFormControl.value!,
                livesAt: this.livesAtFormControl.value!,
            },
            this.token!,
            'response',
        );
        request
            .pipe(
                catchError((e: HttpErrorResponse) => {
                    if (Array.isArray(e.error?.errors)) {
                        this.snackBar.open(e.error.errors.join(', '), 'Close', {duration: 5000});
                        return [];
                    }

                    if (!('signupTeamResult' in e.error)) {
                        const errorMessage =
                            typeof e.error === 'string' ? e.error : 'Failed to register team; invalid request';
                        this.snackBar.open(errorMessage, 'Close', {duration: 5000});
                        return [];
                    }

                    const response = e.error as TournamentSignupTeamResponseDto;
                    switch (response.signupTeamResult) {
                        case TournamentSignupTeamResponseDto.SignupTeamResultEnum.TeamAlreadyExists:
                            this.nameFormControl.setErrors({teamAlreadyExists: true});
                            return of();
                        case TournamentSignupTeamResponseDto.SignupTeamResultEnum.MaxParticipantsReached:
                            this.nameFormControl.setErrors({maxParticipantsReached: true});
                            return of();
                        case TournamentSignupTeamResponseDto.SignupTeamResultEnum.RegistrationClosed:
                            this.nameFormControl.setErrors({registrationClosed: true});
                            return of();
                        default:
                            return [];
                    }
                }),
                finalize(() => (this.submitting = false)),
            )
            .subscribe(() => (this.success = true));
    }

    protected readonly LivesAtEnum = LivesAtEnum;
}
